import React, { useState, useEffect } from "react";
import { useParams } from "react-router";
import { FormattedMessage } from "react-intl";
import { Space, Spinner, Button } from "@masterteam/mt-design-react";
import { useSelector } from "react-redux";

import api from "../../providers/api";
import { ReactComponent as InfoIcon } from "../../assets/images/kpi-info.svg";
import { ReactComponent as AttachmentIcon } from "../../assets/images/attachment.svg";
import { ReactComponent as ListIcon } from "../../assets/images/list.svg";
import { ReactComponent as CommentIcon } from "../../assets/images/comment.svg";
import { ReactComponent as HistoryIcon } from "../../assets/images/kpi-request.svg";
import { ReactComponent as ChangeRequestsIcon } from "../../assets/images/kpi-change-requests.svg";

import Breadcrumb from "../Breadcrumb";
import Header from "./Header";
import InfoDetails from "./InfoDetails";
import KPIAttachments from "./KPIAttachments";
import KPIHistory from "./KPIHistory";
import CommentsList from "./CommentsList";
import HistoricalInfo from "./HistoricalInfo";
import ApproveRejectModal from "./ApproveRejectModal";
import KPIChangeRequests from "./KPIChangeRequests";

import { REQUEST_STATUSES } from "../../configs/enums";

import { addToast } from "../../helpers";
import "./style.scss";

function KPIDetails() {
  const [KPIDetails, setKPIDetails] = useState({});

  const [loading, setLoading] = useState(true);
  const [componentIndex, setComponentIndex] = useState(0);
  const [attachments, setAttachments] = useState({ loading: true, data: [] });
  const [requests, setRequests] = useState({ loading: true, data: [] });
  const [comments, setComments] = useState({ loading: true, data: [] });
  const [changeRequests, setChangeRequests] = useState({
    loading: true,
    data: []
  });
  const [showModal, setShowModal] = useState(false);

  const [historicalInfo, setHistoricalInfo] = useState({
    loading: false,
    data: []
  });

  const objectiveLevels = useSelector(
    (store) => store.theme.ObjectiveLevel.item.Items
  );

  const params = useParams();

  function onCloseModal() {
    setShowModal(false);
  }

  async function fetchData() {
    setLoading(true);
    try {
      const kpiRes = await api.kpi.getById(params.id);
      const { Data: kpi } = kpiRes;
      setKPIDetails({
        ...kpi,
        Status: kpi.Status,
        ID: kpi?.ID
      });
    } catch (err) {
      addToast.error(err.message, {
        toastId: "get-users-error"
      });
    } finally {
      setLoading(false);
    }
  }

  const fetchComments = async () => {
    setComments((currentState) => ({
      ...currentState,
      loading: true
    }));
    try {
      const { Data: commentsData } = await api.kpi.getComments(params.id);
      setComments({
        data: commentsData,
        loading: false
      });
    } catch (err) {
      setComments((currentState) => ({
        ...currentState,
        loading: false
      }));
      addToast.error(err.message, {
        toastId: "get-users-error"
      });
    }
  };

  const fetchAttachments = async () => {
    setAttachments((currentState) => ({
      ...currentState,
      loading: true
    }));
    try {
      const { Data: attachmentsData } = await api.kpi.getKPIAttachments(
        params.id
      );
      setAttachments({
        data: attachmentsData,
        loading: false
      });
    } catch (err) {
      setAttachments((currentState) => ({
        ...currentState,
        loading: false
      }));
      addToast.error(err.message, {
        toastId: "get-users-error"
      });
    }
  };

  const fetchRequests = async () => {
    setRequests((currentState) => ({
      ...currentState,
      loading: true
    }));
    try {
      const { Data: requestsData } = await api.workflow.getByKpiID(params.id);
      setRequests(requestsData);
      setRequests({
        data: requestsData,
        loading: false
      });
    } catch (err) {
      setRequests((currentState) => ({
        ...currentState,
        loading: true
      }));
      addToast.error(err.message, {
        toastId: "get-users-error"
      });
    }
  };

  const fetchChangeRequests = async () => {
    setChangeRequests((currentState) => ({
      ...currentState,
      loading: true
    }));
    try {
      const { Data: requestsData } = await api.kpi.getChangeRequests(params.id);
      setChangeRequests({
        data: requestsData,
        loading: false
      });
    } catch (err) {
      setChangeRequests((currentState) => ({
        ...currentState,
        loading: true
      }));
      addToast.error(err.message, {
        toastId: "get-users-error"
      });
    }
  };

  const fetchHistoricalInfo = async () => {
    setHistoricalInfo((currentState) => ({
      ...currentState,
      loading: true
    }));
    try {
      const res = await api.kpi.getHistoricalInformation(params.id);

      setHistoricalInfo({
        data: res?.Data,
        loading: false
      });
    } catch (err) {
      setHistoricalInfo((currentState) => ({
        ...currentState,
        loading: true
      }));
      addToast.error(err.message, {
        toastId: "get-users-error"
      });
    }
  };

  function getAllData() {
    fetchData();
    fetchComments();
    fetchAttachments();
    fetchRequests();
    fetchChangeRequests();
    fetchHistoricalInfo();
  }

  useEffect(() => {
    getAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  const componentsList = [
    {
      name: "info",
      loading,
      component: (
        <InfoDetails
          getData={fetchData}
          KPIDetails={KPIDetails}
          kpisStatus={KPIDetails.KPIType?.Status}
        />
      ),
      Icon: InfoIcon
    },
    {
      name: "attachments",
      loading: attachments.loading,
      component: (
        <KPIAttachments
          attachments={attachments.data}
          loading={attachments.loading}
          KPIDetails={KPIDetails}
          getData={fetchAttachments}
        />
      ),
      Icon: AttachmentIcon
    },
    {
      name: "requests",
      counts:
        requests?.data?.filter(
          (_item) => _item.Status === REQUEST_STATUSES.PENDING
        ).length || 0,
      showCounts: true,
      loading: requests.loading,
      component: (
        <KPIHistory
          requests={requests.data}
          loading={requests.loading}
          KPIDetails={KPIDetails}
          getData={fetchRequests}
        />
      ),
      Icon: ListIcon
    },
    {
      name: "historical",
      loading: historicalInfo.loading,
      component: (
        <HistoricalInfo
          data={historicalInfo.data}
          loading={historicalInfo.loading}
        />
      ),
      Icon: HistoryIcon
    },
    {
      name: "comments",
      loading: comments.loading,
      component: (
        <CommentsList
          KPIDetails={KPIDetails}
          comments={comments.data}
          loading={comments.loading}
          getData={fetchComments}
          callback={(data) => {
            setComments((currentComments) => ({
              ...currentComments,
              data: data
            }));
          }}
        />
      ),
      Icon: CommentIcon
    },
    {
      name: "changeRequests",
      loading: changeRequests.loading,
      component: (
        <KPIChangeRequests
          changeRequests={changeRequests.data}
          loading={changeRequests.loading}
          KPIDetails={KPIDetails}
          getData={fetchChangeRequests}
        />
      ),
      Icon: ChangeRequestsIcon
    }
  ];

  const level = objectiveLevels?.find(
    (e) => +e?.Value === +KPIDetails?.Objective?.Level
  );

  let breadcrumbData = [
    {
      ...KPIDetails?.Objective?.Perspective?.Strategy,
      path: `/splus/strategy-map/${KPIDetails?.Objective?.Perspective?.Strategy?.ID}`
    },
    +KPIDetails?.Objective?.StrategyType === 1
      ? KPIDetails?.Objective?.Theme
      : KPIDetails?.Objective?.Pillar,
    {
      ...KPIDetails?.Objective,
      path: `/splus/objective-details/${KPIDetails?.Objective?.ID}`
    },
    // { ArabicName: level?.Arabic, EnglishName: level?.English }, // Level
    KPIDetails,
    <FormattedMessage id="kpi.kpi-details" />
  ];

  if (KPIDetails?.Objective?.ParentID)
    breadcrumbData.splice(2, 0, {
      ...KPIDetails?.Objective.Parent,
      path: `/splus/objective-details/${KPIDetails?.Objective?.Parent?.ID}`
    });

  const canApproveObj = requests?.data?.find((e) => e?.CanApprove);

  return loading && !KPIDetails?.ID ? (
    <div className="loader-container">
      <Spinner size="large" />
    </div>
  ) : (
    <>
      <Space
        direction="vertical"
        size="large"
        className="kpi-details-container"
      >
        <Breadcrumb
          data={breadcrumbData}
          fontSize="var(--font-large)"
          endContent={
            canApproveObj ? (
              <Space align="center">
                <Button variant="primary" onClick={() => setShowModal(true)}>
                  <FormattedMessage id="common.kpi-update-details" />
                </Button>
              </Space>
            ) : null
          }
        />
        <Header
          KPIDetails={KPIDetails}
          navItems={componentsList}
          setComponentIndex={(_index) => setComponentIndex(_index)}
          componentIndex={componentIndex}
        />

        {componentsList[componentIndex]?.component}
      </Space>
      {showModal ? (
        <ApproveRejectModal
          isOpen
          onCloseModal={onCloseModal}
          data={canApproveObj}
          refetchData={getAllData}
        />
      ) : null}
    </>
  );
}

export default KPIDetails;
